import { Injectable } from '@angular/core';
import { BaseApi } from '../_base-api';
import {
  AttributeDetail,
  AttributeEntity,
  BrandEntity,
  CategoryItem,
  ProductDropdownItem,
  ProductEntity,
  ProductItem,
  VariantUpdate,
} from '@models';
import { map, Observable, of } from 'rxjs';
import { DataGetFn, DataGetOptions, ItemRecord } from '@controladad/ng-base';
import { GetApiAdapter } from '../../adapters';
import { IProductApiService } from './product-api.interface';
import { joinNamesFn } from '../../helpers';

@Injectable({
  providedIn: 'root',
})
export class AdminProductsApiService extends BaseApi<ProductEntity> implements IProductApiService {
  constructor() {
    super('/AdminPanel/Product', {
      get: '/AdminPanel/GetProducts',
    });
  }

  override get(id: number): Observable<ProductEntity> {
    return http$.get<ProductEntity>(`/AdminPanel/GetProduct?ProductId=${id}`);
  }

  // @ts-ignore
  override getAdapter(options?: DataGetOptions<ProductItem>) {
    return GetApiAdapter('/AdminPanel/GetProducts', options);
  }

  // @ts-ignore
  override create(model: any) {
    return http$.post('/AdminPanel/AddProduct', model);
  }

  // @ts-ignore
  override update(model: any) {
    return http$.put('/AdminPanel/EditProduct', model);
  }

  // @ts-ignore
  override delete(ids: number[]) {
    return http$.delete('/AdminPanel/DeleteProducts', { body: { productIds: ids } });
  }

  changeStatus(enable: boolean, ids: number[]) {
    return http$.patch('/AdminPanel/ChangeProductState', { ids, state: enable ? 1 : 0 });
  }

  changeApproval(approved: boolean, ids: number[]) {
    return http$.patch('/AdminPanel/ChangeProductStatusForAdmin', { ids, productStatus: approved ? 1 : 2 });
  }

  getAllVendors(): Observable<ItemRecord<number>[]> {
    return super
      .getAll<{ id: number; vendorName: string }>(undefined, '/AdminPanel/GetVendorList')
      .pipe(map((vendors) => vendors.map((v) => ({ value: v.id, label: v.vendorName }))));
  }

  addVariants(data: { productId: number; variants: VariantUpdate[] }) {
    return http$.post('/AdminPanel/CreateVariant', data);
  }

  deleteVariants(variantIds: number[]) {
    return http$.delete('/AdminPanel/DeleteVariants', { body: { variantIds } });
  }

  getBrandItems(): Observable<ItemRecord<number>[]> {
    return super
      .getAll<BrandEntity>(undefined, '/AdminPanel/GetBrandList')
      .pipe(map((brands) => brands.map((b) => ({ label: b.name.english, value: b.id }))));
  }

  getAttributeItemRecords(categoryIds: number[]) {
    const nameFn = joinNamesFn(' - ');
    const ids = new URLSearchParams();
    categoryIds.forEach((id) => ids.append('CategoryIds', id.toString()));

    return this.getAll<AttributeEntity>(undefined, `/AdminPanel/GetAttributes?${ids}`).pipe(
      map((item) => item.map((record) => ({ label: nameFn(record.name), value: record.id }))),
    );
  }

  getAttributeTermItemRecords(id: number) {
    return http$
      .get<AttributeDetail>(`/AdminPanel/GetAttribute?AttributeId=${id}`)
      .pipe(map((value) => value.values.map((term) => ({ value: term.id, label: joinNamesFn(' - ')(term.value) }))));
  }

  getCategories(): Observable<CategoryItem[]> {
    return super.getAll<CategoryItem>(undefined, '/AdminPanel/GetCategoryList');
  }

  getProductNamesAdapter(options?: DataGetOptions<ProductDropdownItem>): DataGetFn<ProductDropdownItem> {
    return GetApiAdapter('/AdminPanel/GetProductNames', options);
  }

  getProductDropdownItems(productIds: number[]): Observable<ProductDropdownItem[]> {
    if (productIds.length === 0) {
      return of([]);
    }

    return this.getAll<ProductDropdownItem>({
      filters: [{ key: 'ProductIds', value: productIds }],
    }, `/AdminPanel/GetProductNames`);
  }

  exportProducts(filters?: any) {
    return this.exportAndDownload('/AdminPanel/ExportProductList', filters, 'products.xlsx');
  }
}